import { useState } from 'react';
import { useMutation, useSubscription } from '@apollo/client';
import { Center } from '@chakra-ui/react';

import {
  ShineStartFormCompletedStatus,
  ShineStartStatus,
} from '__generated__/GQL';
import config from 'config';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import { throwOnlyUnhandledErrors } from 'common/hooks/useErrorHandledMutation';
import webView from 'common/webView';
import Loader from 'components/Loader';
import Typeform from 'components/Typeform';
import { logEvent } from 'features/Analytics/analytics';
import useFinishProfileSetupMutation from 'features/Onboarding/common/useFinishProfileSetupMutation';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import { throwIfErrorPresent } from 'features/Onboarding/utils';

import useCompanyContext from '../../../../../common/hooks/useCompanyContext';
import { TrackSegmentEventDocument } from '../../../../Analytics/graphql/mutations/trackSegmentEvent.gql';
import { OnShineStartFormCompletedDocument } from './graphql/subscriptions/onShineStartFormCompleted.gql';
import { OnUpdatedShineStartDocument } from './graphql/subscriptions/onUpdatedShineStart.gql';
import useTypeformDataQuery from './useTypeformDataQuery';

interface ShineStartBusinessInfoProps {
  onFormRefused: () => void;
  onFormCompleted: () => void;
}

const ShineStartBusinessInfo = asBentoPage<ShineStartBusinessInfoProps>(
  ({ Layout, onFormCompleted, onFormRefused, onPrev }) => {
    const { companyProfileId } = useCompanyContext();
    const [isWaitingForCompletion, setIsWaitingForCompletion] = useState(false);

    const { loading: typeformDataLoading, typeformData } =
      useTypeformDataQuery();

    const onTypeformSubmit = () => {
      setIsWaitingForCompletion(true);
    };

    const isLoading = typeformDataLoading || isWaitingForCompletion;

    const { finishProfileSetup } = useFinishProfileSetupMutation();
    const [trackApplicantProfileCompleted] = useMutation(
      TrackSegmentEventDocument,
      {
        variables: {
          input: {
            companyProfileId,
            event: 'Applicant Profile Completed',
            properties: {
              accountType: 'shine-start',
              isMultiKYC: false, // shine-starts don’t have beneficiaries
              withWebView: webView.isInWebView,
              // props legalForm and isMicro will be added directly from graphql
            },
          },
        },
      },
    );

    // Keeping both subscriptions for backward compatibility while we migrate the shine start form submission flow
    useSubscription(OnUpdatedShineStartDocument, {
      onData: ({ data: subscriptionData }) => {
        const { data, error } = subscriptionData;

        throwIfErrorPresent(error);

        const shineStartStatus = data?.updatedShineStart.status;
        logEvent({ name: 'Shine_Start_Form_Completed' });

        if (shineStartStatus === ShineStartStatus.Refused) {
          onFormRefused();
        } else if (shineStartStatus === ShineStartStatus.FormCompleted) {
          trackApplicantProfileCompleted();

          finishProfileSetup()
            .then(() => onFormCompleted())
            .catch(throwOnlyUnhandledErrors);
        }
      },
    });

    useSubscription(OnShineStartFormCompletedDocument, {
      onData: ({ data: subscriptionData }) => {
        const { data, error } = subscriptionData;

        throwIfErrorPresent(error);

        const shineStartStatus = data?.shineStartFormCompleted.status;
        logEvent({ name: 'Shine_Start_Form_Completed' });

        if (shineStartStatus === ShineStartFormCompletedStatus.Rejected) {
          onFormRefused();
        } else if (
          shineStartStatus === ShineStartFormCompletedStatus.FormCompleted
        ) {
          trackApplicantProfileCompleted();

          finishProfileSetup()
            .then(() => onFormCompleted())
            .catch(throwOnlyUnhandledErrors);
        }
      },
    });

    return (
      <Layout nav={<OBNavigation onPrev={isLoading ? undefined : onPrev} />}>
        {isLoading ? (
          <Center height="full">
            <Loader />
          </Center>
        ) : (
          <Typeform
            initialData={typeformData}
            onSubmit={onTypeformSubmit}
            typeformId={config.shineStartTypeformId}
          />
        )}
      </Layout>
    );
  },
);

export default ShineStartBusinessInfo;
